import { Divider, Form } from 'antd';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import { DefaultRouteTabs } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import ManagerialInvoicingForm from '@/components/Reports/ManagerialReports/ManagerialInvoicingForm';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import {
  AverageTicketOptionType,
  ManagerialIncomeFormValues,
  ManagerialIncomeRequest,
  ManagerialIncomeResponseType,
} from '@/core/domain/NetReport/Managerial/Managerial.types';
import { useManagerialIncome } from '@/core/domain/NetReport/Managerial/Managerial.hooks';
import { useReportGenerate } from '@/hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { DateTimeFormat } from '@/utils/moment';
import { convertParams } from '@/utils/queryParams';
import moment from 'moment';
import { useEffect } from 'react';

export default function ManagerialInvoicingPage({ route }: PageProps) {
  const [form] = Form.useForm<ManagerialIncomeFormValues>();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const params = convertParams<ManagerialIncomeFormValues>({
    initialDate: 'string',
    finalDate: 'string',
    companiesIds: 'array-number',
    requestType: 'string',
  });

  useEffect(() => {
    const { initialDate, finalDate, ...rest } = params;

    if (initialDate) form.setFieldValue('initialDate', moment(initialDate));

    if (finalDate) form.setFieldValue('finalDate', moment(finalDate));

    if (rest) {
      form.setFieldsValue(rest);
    }
  }, []);

  const generateReport = useManagerialIncome();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const values = await form.validateFields();

      const averageTicketOption = (
        selectedOptions?: AverageTicketOptionType[],
      ) => {
        if (!!selectedOptions?.length) {
          if (
            selectedOptions.includes(AverageTicketOptionType.workOrder) &&
            selectedOptions.includes(AverageTicketOptionType.sale)
          ) {
            return AverageTicketOptionType.all;
          }
          if (selectedOptions.length === 1) {
            return selectedOptions[0];
          }
        }

        return undefined;
      };

      const obj: ManagerialIncomeRequest = {
        responseType:
          format === 'CSV'
            ? ManagerialIncomeResponseType.csv
            : ManagerialIncomeResponseType.pdf,
        requestType: values.requestType,
        companiesIds: values.companiesIds ? values.companiesIds.toString() : '',
        startDate: values.initialDate.format(DateTimeFormat),
        endDate: values.finalDate.format(DateTimeFormat),
        format: format,
        costCenterIds: values.costCenterIds
          ? values.costCenterIds?.toString()
          : undefined,
        averageTicketOption: averageTicketOption(values?.averageTicketOption),
        customerPersonTypes: values.customerPersonTypes,
      };

      const result = await generateReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_faturamento.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_faturamento.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_faturamento.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={useIntl().formatMessage({ id: route.name })} />

      <ManagerialInvoicingForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}
